import React, { useRef, Children, memo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
export function ModulesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="modules" ref={ref} className="w-full bg-navy text-white py-8 md:py-16 px-4" data-id="element-213">
      <div className="max-w-4xl mx-auto" data-id="element-214">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8 md:mb-12" data-id="element-215">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6" data-id="element-216">
            INTRODUCING THE MEGA MUSIC PACK
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto" data-id="element-217">
            The Complete Annual Curriculum for Music Educators — Pre-K Through
            Grade 12.
          </p>
          <p className="text-base md:text-lg mt-4 md:mt-6 italic text-gold-light" data-id="element-218">
            Everything inside is editable. Add your name. Add your school.
            Teach.
          </p>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="mt-8 md:mt-12" data-id="element-219">
            <img src="https://cdn.magicpatterns.com/uploads/kaw2s3R6nFZXj9wuxPbcLn/oBVwo2uhP3MbVgZKZA3Ri.png" alt="Children learning music with piano, guitar, drums and various instruments" className="w-full max-w-3xl mx-auto rounded-lg shadow-2xl" data-id="element-220" />
          </motion.div>
        </motion.div>

        <div className="space-y-6 md:space-y-8" data-id="element-221">
          <ModuleCard title="THE MASTER GUIDE" delay={0.1} isInView={isInView} data-id="element-222">
            <p data-id="element-223">
              The complete methodology behind the pack — the Four Pillars of
              Music Education, classroom management systems, and differentiation
              strategies.
            </p>
          </ModuleCard>

          <ModuleCard title="PRE-K & KINDERGARTEN — Ages 3-6" delay={0.2} isInView={isInView} image="https://cdn.magicpatterns.com/uploads/6i7GpSXLuuugaWbAF5hVxa/sEcA9dyfoZ1Uu0xRFhTVG.png" imageAlt="Young children playing with percussion instruments and maracas" data-id="element-224">
            <p data-id="element-225">
              Complete weekly lesson plans. Twenty tested activities. Seven core
              songs. DIY instrument guide. Assessment system.
            </p>
          </ModuleCard>

          <ModuleCard title="ELEMENTARY — Grades 1-6" delay={0.3} isInView={isInView} image="https://cdn.magicpatterns.com/uploads/7x7wneuzvSAmr8fJRwZTq6/asCTHuJgxnCnzVzszmbdu.png" imageAlt="Elementary students doing rhythm and movement activities" data-id="element-226">
            <p data-id="element-227">
              Full three-trimester curriculum. Recorder guide. Music reading
              system. Songwriting and production projects.
            </p>
          </ModuleCard>

          <ModuleCard title="MIDDLE & HIGH SCHOOL — Grades 7-12" delay={0.4} isInView={isInView} image="https://cdn.magicpatterns.com/uploads/bKq1zGTC3CnWjo6mmUJiu7/dDcLjtBYO3_pyMO-AESZb.png" imageAlt="Middle and high school students performing and clapping together" data-id="element-228">
            <p data-id="element-229">
              Music identity. Chord charts. Guitar and keyboard skills. DAW
              production. Music history survey. EP capstone project.
            </p>
          </ModuleCard>

          <ModuleCard title="100+ ACTIVITIES BANK" delay={0.5} isInView={isInView} data-id="element-230">
            <p data-id="element-231">
              Every activity tagged by level, duration, materials, and
              objective. Lesson planning in under five minutes.
            </p>
          </ModuleCard>

          <ModuleCard title="ASSESSMENTS FOLDER" delay={0.6} isInView={isInView} data-id="element-232">
            <p data-id="element-233">
              Twelve complete rubrics. Student portfolios. 3-Minute Teacher
              Tracking System. Report cards ready before you need them.
            </p>
          </ModuleCard>

          <ModuleCard title="SPECIAL PROJECTS & EVENTS" delay={0.7} isInView={isInView} data-id="element-234">
            <p data-id="element-235">
              End-of-Year Festival guide. World Music Week. Spring Concert
              production. Classroom Podcast project. School Anthem capstone.
            </p>
          </ModuleCard>
        </div>
      </div>
    </section>;
}
interface ModuleCardProps {
  title: string;
  children: React.ReactNode;
  delay: number;
  isInView: boolean;
  image?: string;
  imageAlt?: string;
}
function ModuleCard({
  title,
  children,
  delay,
  isInView,
  image,
  imageAlt
}: ModuleCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.6,
    delay
  }} className="bg-white bg-opacity-10 p-4 md:p-6 rounded-lg border-l-4 border-gold" data-id="element-236">
      <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3 text-gold-light" data-id="element-237">
        {title}
      </h3>
      <div className="text-base md:text-lg leading-relaxed mb-4" data-id="element-238">
        {children}
      </div>
      {image && <motion.img initial={{
      opacity: 0,
      scale: 0.95
    }} animate={isInView ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      delay: delay + 0.2
    }} src={image} alt={imageAlt} className="w-full rounded-lg shadow-lg mt-4" data-id="element-239" />}
    </motion.div>;
}